import { MESApiService } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	TIMELINE_PERMISSION,
	type Billet,
	type Campaign,
	type Interval,
	type Passage,
	type RollChange,
	type Stop,
	type TimeRange,
	type TimelineData,
	type WorkOrder,
} from "../models";
import { rangeError, utcSqlTimestamp } from "../timeline";

type RawInterval<T extends Interval> = Omit<T, "start" | "end"> & {
	Start: string;
	End?: string | null;
};
function timestamp(value: string): number {
	if (!value.endsWith("Z") || !Number.isFinite(Date.parse(value)))
		throw new Error("The timeline returned an invalid UTC timestamp.");
	return Date.parse(value);
}
function interval<T extends Interval>(row: RawInterval<T>): T {
	const { Start, End, ...fields } = row;
	return {
		...fields,
		start: timestamp(Start),
		end: End ? timestamp(End) : null,
	} as unknown as T;
}

export async function getOperationsTimeline(
	range: TimeRange,
	signal?: AbortSignal,
): Promise<TimelineData> {
	const error = rangeError(range);
	if (error) throw new Error(error);
	const response = await new MESApiService().callV2(
		`[${TIMELINE_PERMISSION.replace(".", "].[")}]`,
		[
			{ name: "Start", value: utcSqlTimestamp(range.start) },
			{ name: "End", value: utcSqlTimestamp(range.end) },
		],
		signal,
	);
	if (!response.ok)
		throw new Error(
			response.message || "Unable to load the operations timeline.",
		);
	const tables = get(response, "data.tables") as
		| { rows: unknown[] }[]
		| undefined;
	if (!tables || tables.length !== 6)
		throw new Error(
			"The timeline response is incomplete. Refresh and try again.",
		);
	const passages = new Map<number, Passage[]>();
	for (const row of tables[1].rows as {
		BilletId: number;
		StandId: number;
		Start: string;
		End: string;
	}[]) {
		const entry = {
			BilletId: row.BilletId,
			StandId: row.StandId,
			start: timestamp(row.Start),
			end: timestamp(row.End),
		};
		const list = passages.get(row.BilletId) ?? [];
		list.push(entry);
		passages.set(row.BilletId, list);
	}
	return {
		billets: (
			tables[0].rows as (RawInterval<Billet> & {
				LastObservedEnd: string | null;
			})[]
		).map((row) => ({
			...interval<Billet>(row),
			lastObservedEnd: row.LastObservedEnd
				? timestamp(row.LastObservedEnd)
				: null,
			passages: passages.get(row.Id) ?? [],
		})),
		campaigns: (
			tables[2].rows as (RawInterval<Campaign> & { NextStart: string | null })[]
		).map((row) => ({
			...interval<Campaign>(row),
			nextStart: row.NextStart ? timestamp(row.NextStart) : null,
		})),
		workOrders: (tables[3].rows as RawInterval<WorkOrder>[]).map(
			interval<WorkOrder>,
		),
		stops: (tables[4].rows as RawInterval<Stop>[]).map(interval<Stop>),
		rolls: (tables[5].rows as RawInterval<RollChange>[]).map(
			interval<RollChange>,
		),
	};
}
